import React, { useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { css } from '@emotion/core'
import { bool, func, string } from 'prop-types'

import { MXWidget } from '@divvy/odin.bank-manager'
import { useMakeTestId } from '@divvy-web/hooks.usenameformatter'
import Modal from '@divvy-web/skylab.modal'

import GetMXWidgetUrl from '@sputnik/pages/gql/GetMXWidgetUrl.gql'
import { logError } from '@sputnik/utils/loggerUtils'

const MoneyMoverMXWidget = ({
  companyId,
  isShowing,
  onClose,
  onMXWidgetManualSelection,
  onReady,
  onSyncError,
  setInstitutionName,
}) => {
  const { appId } = useParams()
  const [isConnecting, setIsConnecting] = useState(false)
  const makeTestId = useMakeTestId('MoneyMoverMXWidget')
  const {
    data,
    error,
    loading: isLoading,
  } = useQuery(GetMXWidgetUrl, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !companyId,
    variables: {
      companyId,
      isMobile: false,
      mode: 'verification',
    },
  })
  const widgetUrl = data?.bankIntegrations?.widgetUrl

  useEffect(() => {
    if (error && !isLoading) {
      logError(error, {
        attributes: {
          action: 'getMxWidgetUrlError',
          creditAppId: appId,
          result: error,
        },
        eventName: 'GetMXWidgetUrl',
      })
      onSyncError()
    }
  }, [error, isLoading, appId, onSyncError])

  const handleMXWidgetError = (widgetError, widgetInfo) => {
    logError(widgetError, {
      attributes: {
        action: 'mxWidgetError',
        info: widgetInfo,
        creditAppId: appId,
        result: widgetError,
      },
      eventName: 'LoadMXWidget',
    })
    onSyncError()
  }

  if (error && !isLoading) return null

  return (
    <ErrorBoundary
      FallbackComponent={() => <br />}
      onError={handleMXWidgetError}
    >
      <Modal
        css={modalCss}
        dataTestId={makeTestId('Modal')}
        isShowing={isShowing}
        title=' '
        onCloseClick={isConnecting ? null : onClose}
      >
        <MXWidget
          isLoadingWidgetUrl={isLoading}
          renderInsideModal={false}
          setInstitutionName={setInstitutionName}
          setIsConnecting={setIsConnecting}
          widgetUrl={widgetUrl}
          widgetUrlError={error}
          onBankAccountConnected={onReady}
          onClose={onClose}
          onOpenAddManualBankAccount={onMXWidgetManualSelection}
        />
      </Modal>
    </ErrorBoundary>
  )
}

MoneyMoverMXWidget.propTypes = {
  companyId: string,
  isShowing: bool.isRequired,
  onClose: func.isRequired,
  onMXWidgetManualSelection: func,
  onReady: func.isRequired,
  onSyncError: func.isRequired,
  setInstitutionName: func.isRequired,
}

const modalCss = ({ mq }) => css`
  .Modal-main {
    width: 500px;
    min-height: 700px;
  }

  .Modal-content {
    ${mq.xSmallMaxWidth({
      paddingLeft: 'var(--tri-space-200)',
      paddingRight: 'var(--tri-space-200)',
    })}
  }
`

export default MoneyMoverMXWidget
